import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import { visuallyHidden } from '@mui/utils'
import { isTextUIPart, isToolUIPart } from 'ai'
import type { ChatStatus } from 'ai'
import type { AssistantMessage } from './AssistantProvider'
import { ASSISTANT_ANNOUNCEMENTS } from './copy'

/** Long answers are cut here; the full text stays in the conversation. */
const MAX_SPOKEN_LENGTH = 400

const spokenAnswer = (message: AssistantMessage | undefined): string => {
  if (!message || message.role !== 'assistant') return ''
  if (
    message.parts.some(
      part => isToolUIPart(part) && part.state === 'approval-requested'
    )
  ) {
    return ASSISTANT_ANNOUNCEMENTS.approval
  }
  const text = message.parts
    .filter(isTextUIPart)
    .map(part => part.text)
    .join(' ')
    .replace(/[*_`#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!text) return ASSISTANT_ANNOUNCEMENTS.ready
  return text.length > MAX_SPOKEN_LENGTH
    ? `${text.slice(0, MAX_SPOKEN_LENGTH).trimEnd()}…`
    : text
}

interface AssistantAnnouncerProps {
  messages: AssistantMessage[]
  status: ChatStatus
  /**
   * The conversation (its Chat), so an answer that finished while no
   * announcer was there (the docked panel unmounts while minimised) is said
   * when it is back on screen.
   */
  conversation?: object
}

// Conversations whose answer was still coming in when their announcer went.
const missedAnswers = new WeakSet<object>()

const isAnswering = (status: ChatStatus) =>
  status === 'submitted' || status === 'streaming'

/**
 * Screen-reader voice for the conversation. Streaming answers arrive a few
 * words at a time, so rather than reading every chunk this says "thinking"
 * once and then the finished answer (or that a change needs approval).
 * Errors are announced by their own alert.
 */
const AssistantAnnouncer = ({
  messages,
  status,
  conversation,
}: AssistantAnnouncerProps) => {
  const [announcement, setAnnouncement] = useState('')
  const previousStatus = useRef(status)

  useEffect(() => {
    const before = previousStatus.current
    previousStatus.current = status
    // Back to a conversation whose answer finished while no announcer was
    // there: say it now. Still coming in, the change to ready says it.
    const missed = Boolean(conversation && missedAnswers.delete(conversation))
    if (status === 'submitted' && before !== 'submitted') {
      setAnnouncement(ASSISTANT_ANNOUNCEMENTS.thinking)
    } else if (status === 'ready' && (missed || isAnswering(before))) {
      setAnnouncement(spokenAnswer(messages[messages.length - 1]))
    }
  }, [messages, status, conversation])

  // Gone mid-answer (the panel minimised): say it when the chat is back.
  useEffect(
    () => () => {
      if (conversation && isAnswering(previousStatus.current)) {
        missedAnswers.add(conversation)
      }
    },
    [conversation]
  )

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="assistant-announcer"
      sx={visuallyHidden}
    >
      {announcement}
    </Box>
  )
}

export default AssistantAnnouncer
